import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { map } from 'rxjs/operators';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';

import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { FiltersService } from '../../../../../permissions/services/filters.service';
import { StoreAuxService } from '../../../../../services/storeAux/store-aux.service';
import { HasPermissionsService } from '../../../../../shared/services/has-permissions/has-permissions.service';
import { CatalogItem, NewPurchaseOrderConfig } from '../../../../models/makers-finances-mgr.models';
import { environment } from '../../../../../../environments/environment';
import { DETAIL_MODE, DetailMode, SourceType, PO_FIELD, PO_FIELD_LABELS, PO_SECTION_LABELS, PO_SECTION } from '../../purchase-order.constants';
import { SelectOption, staticSourceFromCatalog } from '../section-base.types';

@Component({
  selector: 'app-section-general-info',
  templateUrl: './section-general-info.component.html',
  styleUrls: ['./section-general-info.component.scss'],
})
export class SectionGeneralInfoComponent implements OnInit, OnChanges {
  /* istanbul ignore next -- decorator metadata */
  @Input() form: FormGroup;
  /* istanbul ignore next -- decorator metadata */
  @Input() mode: DetailMode;
  /* istanbul ignore next -- decorator metadata */
  @Input() sourceType: SourceType;
  /* istanbul ignore next -- decorator metadata */
  @Input() remoteConfig: NewPurchaseOrderConfig | null;
  /* istanbul ignore next -- decorator metadata */
  @Input() sourceTypeCatalog: CatalogItem[] = [];
  /* istanbul ignore next -- decorator metadata */
  @Input() poTypeCatalog: CatalogItem[] = [];
  /* istanbul ignore next -- decorator metadata */
  @Input() catalogsLoaded = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() submitted = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() fieldConfigs: Record<string, any> = {};
  /* istanbul ignore next -- decorator metadata */
  @Input() lockedSourceType: SourceType | null = null;
  // Flips true once the parent has patched the form from the loaded PO; triggers setSelection on
  // the selects (one-select is not form-bound).
  /* istanbul ignore next -- decorator metadata */
  @Input() poLoaded = false;

  // In edit mode the skeleton stays until the PO detail is also loaded (poLoaded), not just the
  // catalogs. In create mode only catalogsLoaded matters.
  get showSkeleton(): boolean {
    return !this.catalogsLoaded || (this.mode === DETAIL_MODE.EDIT && !this.poLoaded);
  }

  readonly F = PO_FIELD;
  readonly LABELS = PO_FIELD_LABELS;
  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.GENERAL_INFORMATION];

  readonly selectConfig = { search: false, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };
  readonly searchSelectConfig = { search: true, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };
  readonly makerSelectConfig = { search: true, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };

  /* istanbul ignore next -- decorator metadata */
  @ViewChild('ownerSelect') ownerSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('sourceTypeSelect') sourceTypeSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('makerSelect') makerSelect: SelectComponent<SelectOption>;
  /* istanbul ignore next -- decorator metadata */
  @ViewChild('poTypeSelect') poTypeSelect: SelectComponent<SelectOption>;

  selectSources: Record<string, LazyScrollableDataSource<SelectOption>> = {};
  private userEmail = '';

  /* istanbul ignore next -- decorator metadata */
  constructor(
    private readonly financesService: MakersFinancesMgrService,
    private readonly filtersService: FiltersService,
    private readonly storeAux: StoreAuxService,
    private readonly permissionsService: HasPermissionsService,
  ) {}

  ngOnInit(): void {
    this.userEmail = localStorage.getItem('email') || '';

    this.selectSources[PO_FIELD.MAKER_TO_EXECUTE] = new LazyScrollableDataSource<SelectOption>(
      (page, limit, search) =>
        this.filtersService.getEntities(
          this.filtersService.getBody(environment.modules.financialHub, 'maker'),
          this.filtersService.getParams(page + 1, limit, search)
        ).pipe(map(res => res.data.records.map(e => ({ id: String(e.reference_code || e.id), name: e.name }))))
    );

    this.selectSources[PO_FIELD.OWNER] = new LazyScrollableDataSource<SelectOption>(
      (page, limit, search) =>
        this.financesService.searchOwners(this.storeAux.country, page + 1, limit, search || undefined)
          .pipe(map(res => res.data.records.map(o => ({ id: o.email, name: o.email }))))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catalogsLoaded'] && this.catalogsLoaded) {
      setTimeout(() => {
        if (this.mode === DETAIL_MODE.CREATE && this.userEmail && this.ownerSelect) {
          this.ownerSelect.setSelection({ id: this.userEmail, name: this.userEmail });
        }
      });
    }

    if (changes['sourceTypeCatalog'] && this.sourceTypeCatalog.length) {
      this.selectSources[PO_FIELD.SOURCE_TYPE] = staticSourceFromCatalog(this.sourceTypeCatalog);
      if (this.lockedSourceType) {
        const locked = this.sourceTypeCatalog.find(r => r.code === this.lockedSourceType);
        if (locked) {
          setTimeout(() => {
            this.sourceTypeSelect?.setSelection({ id: locked.code, name: locked.name });
          });
        }
      }
    }

    if (changes['poTypeCatalog'] && this.poTypeCatalog.length) {
      this.selectSources[PO_FIELD.PO_TYPE] = staticSourceFromCatalog(this.poTypeCatalog);
    }

    if (changes['sourceType'] && !changes['sourceType'].firstChange
        && this.isPoTypeVisible && this.poTypeCatalog.length) {
      this.selectSources[PO_FIELD.PO_TYPE] = staticSourceFromCatalog(this.poTypeCatalog);
    }

    if (changes['poLoaded'] && this.poLoaded) {
      setTimeout(() => this._prefillSelects());
    }
  }

  // Edit mode: sync the select UIs to the patched form values. The form already holds the codes;
  // the selects just need their display set (one-select is not form-bound).
  private _prefillSelects(): void {
    const sourceCode = this.form.get(PO_FIELD.SOURCE_TYPE)?.value;
    if (sourceCode && this.sourceTypeSelect) {
      const item = this.sourceTypeCatalog.find(r => r.code === sourceCode);
      if (item) {
        this.sourceTypeSelect.setSelection({ id: item.code, name: item.name });
      }
    }

    const makerCode = this.form.get(PO_FIELD.MAKER_TO_EXECUTE)?.value;
    const makerName = this.form.get(PO_FIELD.MAKER_NAME)?.value;
    if (makerCode && this.makerSelect) {
      this.makerSelect.setSelection({ id: makerCode, name: makerName || makerCode });
    }

    const poTypeCode = this.form.get(PO_FIELD.PO_TYPE)?.value;
    if (poTypeCode && this.poTypeSelect) {
      const item = this.poTypeCatalog.find(r => r.code === poTypeCode);
      if (item) {
        this.poTypeSelect.setSelection({ id: item.code, name: item.name });
      }
    }

    const ownerEmail = this.form.get(PO_FIELD.OWNER)?.value;
    if (ownerEmail && this.ownerSelect) {
      this.ownerSelect.setSelection({ id: ownerEmail, name: ownerEmail });
    }
  }

  get isPoTypeVisible(): boolean {
    const cfg = this.fieldConfigs[PO_FIELD.PO_TYPE];
    if (!cfg) {
      return false;
    }
    if (cfg.visible === false) {
      return false;
    }
    if (cfg.visible_by_source) {
      return !!cfg.visible_by_source[this.sourceType];
    }
    return true;
  }

  get isPoTypeDisabled(): boolean {
    return this.mode === DETAIL_MODE.EDIT
      && this.fieldConfigs[PO_FIELD.PO_TYPE]?.editable_by_source?.[this.sourceType] === false;
  }

  get sourceTypeSelectDisabled(): boolean {
    if (this.lockedSourceType) {
      return true;
    }
    if (this.mode === DETAIL_MODE.EDIT
        && this.fieldConfigs[PO_FIELD.SOURCE_TYPE]?.editable_by_source?.[this.sourceType] === false) {
      return true;
    }
    return false;
  }

  get isParentPoVisible(): boolean {
    return !!this.fieldConfigs[PO_FIELD.PARENT_PO]?.visible_by_source?.[this.sourceType];
  }

  get isParentPoDisabled(): boolean {
    const permission = this.fieldConfigs[PO_FIELD.PARENT_PO]?.editable_by_permission;
    if (!permission) {
      return false;
    }
    return !this.permissionsService.checkPermission(permission);
  }

  onSelectChange(fieldKey: string, value: SelectOption): void {
    console.log(`[section-general-info] onSelectChange("${fieldKey}")`, value);
    this.form.get(fieldKey)?.setValue(value?.id ?? null);
    console.log(`[section-general-info] form.get("${fieldKey}").value after set:`, this.form.get(fieldKey)?.value);
  }

  onMakerChange(value: SelectOption): void {
    this.form.get(PO_FIELD.MAKER_TO_EXECUTE)?.setValue(value?.id ?? null);
    this.form.get(PO_FIELD.MAKER_NAME)?.setValue(value?.name ?? null);
  }

  isInvalid(key: string): boolean {
    if (!this.submitted) {
      return false;
    }
    const ctrl = this.form.get(key);
    return !ctrl?.value || !!ctrl.errors?.['required'];
  }
}
